import React, { useState } from "react";
import { VisibilityIcon, VisibilityOffIcon } from "../../constants/icons";
import Button from "../Buttons/Button";
import colors from "../../constants/colors";

interface LoginFormProps {
  username: string;
  password: string;
  showPassword: boolean;
  helperText: string;
  loading: boolean;
  onUsernameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onShowPasswordToggle: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onForgotPassword: (email: string) => void;
}

const LoginForm: React.FC<LoginFormProps> = ({
  username,
  password,
  showPassword,
  helperText,
  loading,
  onUsernameChange,
  onPasswordChange,
  onShowPasswordToggle,
  onSubmit,
  onForgotPassword,
}) => {
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string>("");

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handleForgotPasswordSubmit = async () => {
    if (!email) {
      setError("Please enter your email.");
      return;
    }

    setError(""); // Clear any previous error message

    try {
      const response = await fetch("https://telegaruda-api.a2zhealth.in/v1/users/forgot-password/email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": "feafbab7cb630d7110a81685b90c4b82",
        },
        body: JSON.stringify({
          email,
          role: "provider",
        }),
      });

      const result = await response.json();

      if (response.ok) {
        // Handle success
        alert(result.message || "OTP sent successfully.");
        setIsForgotPassword(false);
      } else {
        // Handle error
        setError(result.message || "Something went wrong. Please try again.");
      }
    } catch (error) {
      setError("Network error. Please try again.");
    }
  };

  return (
    <form className="w-full max-w-sm mx-auto space-y-4" onSubmit={onSubmit}>
      {!isForgotPassword ? (
        <>
          <div className="relative">
            <label className="block text-sm text-gray-700 mb-1">Username</label>
            <input
              type="text"
              value={username}
              onChange={onUsernameChange}
              placeholder="Enter your username"
              className="text-sm border border-gray-300 rounded p-3 w-full"
            />
            <p className="text-xs text-red-500">{helperText}</p>
          </div>

          <div className="relative">
            <label className="block text-sm text-gray-700 mb-1">Password</label>
            <div className="relative flex items-center">
              <input
                type={showPassword ? "text" : "password"} // Toggle password visibility
                value={password}
                onChange={onPasswordChange}
                placeholder="Enter your password"
                className="text-sm border border-gray-300 rounded p-3 w-full pr-12" // padding-right for the button
              />
              <button
                type="button"
                onClick={onShowPasswordToggle}
                className="absolute right-3"
              >
                {showPassword ? (
                  <VisibilityOffIcon className="text-gray-500" />
                ) : (
                  <VisibilityIcon className="text-gray-500" />
                )}
              </button>
            </div>
            <p className="text-xs text-red-500">{helperText}</p>
          </div>

          <Button
            label={loading ? "Loading..." : "Login"}
            onClick={onSubmit}
            className={`w-full p-3 text-white bg-gradient-to-b from-primary to-secondary rounded hover:bg-orange-600 ${loading ? "bg-gray-400 cursor-not-allowed" : `bg-[${colors.orange}]`}`}
            disabled={loading}
            type="submit"
          />

          <div className="flex justify-center">
            <Button
              type="button"
              style={{ color: colors.orange }}
              className="text-sm hover:underline mt-6"
              onClick={() => setIsForgotPassword(true)} // Show email input when clicked
              label={
                <span className="text-gradient-to-b from-primary to-secondary hover:underline hover:text-black">
                  Forgot Password?
                </span>
              }
            />
          </div>
        </>
      ) : (
        <div className="relative">
          <label className="block text-sm text-gray-700 mb-1">Enter your Email</label>
          <input
            type="email"
            value={email}
            onChange={handleEmailChange}
            placeholder="Enter your email"
            className="text-sm border border-gray-300 rounded p-3 w-full"
          />
          {error && <p className="text-xs text-red-500">{error}</p>} {/* Error message */}
          <div className="flex justify-center mt-4">
            <Button
              label="Send OTP"
              onClick={handleForgotPasswordSubmit}
              className={`w-full p-3 text-white bg-gradient-to-b from-primary to-secondary rounded hover:bg-orange-600 ${loading ? "bg-gray-400 cursor-not-allowed" : `bg-[${colors.orange}]`}`}
              disabled={loading}
            />
          </div>
          <div className="flex justify-center mt-4">
            <Button
              type="button"
              style={{ color: colors.orange }}
              className="text-sm hover:underline"
              onClick={() => setIsForgotPassword(false)}
              label="Back to Login"
            />
          </div>
        </div>
      )}
    </form>
  );
};

export default LoginForm;
